import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';

import { apiClient } from '../utils/apiClient';
import { isNextStep } from '../routing/nextStep';

export interface TwoFactorChallenge {
  requires2FA: true;
  mode: 'setup' | 'verify';
  intentId: string;
  options: PublicKeyCredentialCreationOptionsJSON | PublicKeyCredentialRequestOptionsJSON;
}

export type LoginOutcome =
  | { kind: 'authenticated'; user: ReturnType<typeof buildUser> }
  | { kind: '2fa'; challenge: TwoFactorChallenge };

function buildUser(data: Record<string, unknown>, fallbackEmail?: string) {
  return {
    id: data.id as string,
    email: (data.email as string) || fallbackEmail || '',
    name: `${(data.firstName as string) || ''} ${(data.lastName as string) || ''}`.trim(),
    profilePicture: (data.profilePicture as string) || '',
    verifiedUser: data.verifiedUser as boolean | undefined,
    plan: data.plan as string | undefined,
    hasOnboardingInterests:
      Array.isArray(data.onboardingInterests) && (data.onboardingInterests as unknown[]).length > 0,
    nextStep: isNextStep(data.nextStep) ? data.nextStep : undefined,
  };
}

export const finalizeAuthenticatedUser = (data: Record<string, unknown>, fallbackEmail?: string) => ({
  user: buildUser(data, fallbackEmail),
});

export const login = async (email: string, password: string): Promise<LoginOutcome> => {
  try {
    const response = await apiClient('/users/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Erreur de réseau ou le serveur a retourné une erreur');
    }

    if (data.requires2FA) {
      return {
        kind: '2fa',
        challenge: {
          requires2FA: true,
          mode: data.mode,
          intentId: data.intentId,
          options: data.options,
        },
      };
    }

    if (data.message === 'Connexion réussie') {
      return { kind: 'authenticated', user: buildUser(data, email) };
    }

    throw new Error('Connexion échouée');
  } catch (error) {
    console.error('Erreur lors de la connexion:', error);
    throw error;
  }
};

// Fonction pour s'inscrire
export const register = async ({ email, password, civility, firstName, lastName }: { email: string, password: string, civility: string, firstName: string, lastName: string }) => {
  try {
    const registerResponse = await apiClient('/users/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, civility, firstName, lastName }),
    });

    if (!registerResponse.ok) {
      const errorData = await registerResponse.json();
      throw new Error(errorData.message || 'Erreur lors de la création du compte');
    }

    const registerData = await registerResponse.json();

    // Si l'inscription est réussie, se connecter automatiquement
    if (registerData.message === "Utilisateur créé avec succès") {
      const loginData = await login(email, password);
      return loginData;
    }

    return registerData;
  } catch (error) {
    console.error('Erreur lors de la création du compte:', error);
    throw error;
  }
};

// Fonction pour se déconnecter
export const logout = () => {
  window.dispatchEvent(new CustomEvent('authChange', { detail: { isAuthenticated: false } }));
};

// Fonction pour demander la réinitialisation d'un mot de passe
export const requestPasswordReset = async (email: string) => {
  try {
    const response = await apiClient('/users/request-password-reset', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Erreur lors de la demande de réinitialisation');
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la demande de réinitialisation:', error);
    throw error;
  }
};

// Fonction pour réinitialiser le mot de passe
export const resetPassword = async (resetToken: string, password: string) => {
  try {
    const response = await apiClient('/users/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token: resetToken, password }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Erreur lors de la réinitialisation');
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la réinitialisation:', error);
    throw error;
  }
};
